import { useEffect, useRef, useState } from 'react';
import { Avatar } from '../../components/Avatar';
import { Dialog, ReasonDialog } from '../../components/Dialog';
import { FuzzyDateInput } from '../../components/FuzzyDateInput';
import { Icon } from '../../components/Icon';
import { ReactionBar } from '../../components/Reactions';
import { PersonPicker } from '../../components/PersonPicker';
import { useAnnouncer } from '../../components/Status';
import { formatFuzzyDate } from '../../domain/dates';
import { isAudio, isVideo } from '../../domain/milestones';
import { displayName } from '../../domain/names';
import { anchorPictures, coverAnchorOf, coverOf, coverRight } from '../../domain/covers';
import { isAdmin, isEditor } from '../../domain/permissions';
import type { Account, FamilyPhoto, FuzzyDate, PersonWithNames, UUID } from '../../domain/types';
import { useI18n } from '../../i18n';
import { relativeTime } from '../../lib/time';
import { useRepos } from '../../repositories';
import { useApp } from '../../app/AppState';

/**
 * One moment (a shared photo or an event), viewed in place: the picture, who
 * is in it (anyone can pin people), who shared it, and — for the uploader or
 * an editor — an edit form for the details. Used by the Moments page and by
 * the tree's person card, so tapping a moment never leaves the tree.
 */
export function MomentDialog({
  photo, onClose, onChange, inside,
}: {
  photo: FamilyPhoto | null;
  onClose: () => void;
  /** Called with the updated record after a pin or an edit. */
  onChange?: (p: FamilyPhoto) => void;
  /**
   * Opened from inside the very thing the picture was added to — a recipe's own
   * page, say. "Part of …" would then be a button that leads to where you
   * already are, so it is left out; closing the picture is the way back.
   */
  inside?: boolean;
}) {
  const { t, lang } = useI18n();
  const repos = useRepos();
  const { toast } = useAnnouncer();
  const { me, people, peopleById, actor, reload, moments, stories, recipes, openMemories, openPersonCard, links } = useApp();
  /** Taking a picture down is an administrator's decision, and it asks for a reason. */
  const [removing, setRemoving] = useState(false);
  const admin = actor ? isAdmin(actor.session.role) : false;
  const [accounts, setAccounts] = useState<Map<UUID, Account>>(new Map());
  const [tagPick, setTagPick] = useState<UUID | null>(null);
  const [editing, setEditing] = useState(false);
  const [busy, setBusy] = useState(false);
  const [eTitle, setETitle] = useState('');
  const [eCaption, setECaption] = useState('');
  const [eEvent, setEEvent] = useState(false);
  const [eTakenOn, setETakenOn] = useState<FuzzyDate>({ kind: 'unknown' });

  useEffect(() => {
    void repos.auth.listAccounts().then((list) => setAccounts(new Map(list.map((a) => [a.id, a]))));
  }, [repos]);
  useEffect(() => setEditing(false), [photo?.id]);

  const fail = (res: { ok: false } & Record<string, unknown>) => toast(t(('messageKey' in res ? String(res.messageKey) : 'error.unexpected') as Parameters<typeof t>[0]));

  /** Tagging is a small mode of its own: pick people into a draft, then Save applies the difference. */
  const [tagging, setTagging] = useState(false);
  const [draftIds, setDraftIds] = useState<UUID[]>([]);
  useEffect(() => setTagging(false), [photo?.id]);
  const startTagging = () => {
    setDraftIds(photo?.personIds ?? []);
    setTagPick(null);
    setTagging(true);
  };
  const saveTags = async () => {
    if (!photo) return;
    setBusy(true);
    const before = new Set(photo.personIds);
    const after = new Set(draftIds);
    let latest: FamilyPhoto = photo;
    for (const id of after) if (!before.has(id)) { const r = await repos.photos.tagPerson(photo.id, id, true); if (r.ok) latest = r.value; else fail(r); }
    for (const id of before) if (!after.has(id)) { const r = await repos.photos.tagPerson(photo.id, id, false); if (r.ok) latest = r.value; else fail(r); }
    setBusy(false);
    setTagging(false);
    onChange?.(latest);
    reload();
  };

  const react = async (emoji: string | null) => {
    if (!photo) return;
    const res = await repos.photos.react(photo.id, emoji);
    if (res.ok) {
      onChange?.(res.value);
      reload();
    } else fail(res);
  };

  const canEdit = !!photo && !!actor && (photo.uploadedByAccountId === actor.session.accountId || isEditor(actor.session.role));
  const startEdit = () => {
    if (!photo) return;
    setETitle(photo.title ?? '');
    setECaption(photo.caption ?? '');
    setEEvent(photo.isEvent);
    setETakenOn(photo.takenOn ?? { kind: 'unknown' });
    setEditing(true);
  };
  const saveEdit = async () => {
    if (!photo) return;
    setBusy(true);
    const res = await repos.photos.update(photo.id, { title: eTitle.trim() || undefined, caption: eCaption.trim() || undefined, isEvent: eEvent, takenOn: eTakenOn.kind === 'unknown' ? undefined : eTakenOn });
    setBusy(false);
    if (res.ok) {
      onChange?.(res.value);
      setEditing(false);
      toast(t('photos.detailsSaved'));
      reload();
    } else fail(res);
  };

  const uploader = photo ? accounts.get(photo.uploadedByAccountId)?.displayName ?? '' : '';
  /** Everyone pinned to the picture, and whoever shared it — both as circles, both tappable. */
  const pinned = (photo?.personIds ?? []).map((pid) => peopleById.get(pid)).filter((p): p is PersonWithNames => !!p);
  const uploaderPerson = photo
    ? peopleById.get(links.find((l) => l.accountId === photo.uploadedByAccountId && l.status === 'active')?.personId ?? '')
    : undefined;
  const inPhotoLabel = pinned.length > 0
    ? `${t('photos.inPhoto')}: ${pinned.map((p) => displayName(p, lang)).join(', ')}`
    : t('photos.nobodyPinned');
  const sharedLabel = photo ? t('photos.sharedBy', { name: uploader, when: relativeTime(photo.createdAt, lang) }) : '';
  /*
   * A picture the family added to a day — a simcha, a story, a birth — says so,
   * and the rest of that day is one tap away. Opening it closes this dialog
   * first: two modal dialogs at once would leave the second unreachable.
   */
  /*
   * The main picture. One of a thing's pictures stands for it — a recipe's on
   * the Passed Down shelf and across the top of its page; a story's, a simcha's
   * or a birth's on the family's line and in the middle of its constellation —
   * and this is where that is chosen: while looking at the picture large, which
   * is when anybody knows whether it is the one. It began as a recipe's alone;
   * Jay: *"Can we have the same behavior for stories and anything else that
   * could use a picture for its main node."* The rule is in domain/covers.ts.
   * Only whoever may change the thing is offered it, and only for a still.
   */
  const coverAnchor = photo ? coverAnchorOf(photo) : null;
  const still = !!photo?.storageKey && !!photo.mimeType?.startsWith('image/');
  const isMain = !!photo && !!coverAnchor && still && coverOf(coverAnchor, moments, recipes)?.id === photo.id;
  const mainRight = photo && coverAnchor && actor && still ? coverRight(actor, coverAnchor, { moments, stories, recipes }) : 'none';
  const mayChooseMain = mainRight !== 'none';
  /*
   * A recipe's only picture has always said it is the main one, and still does.
   * A day's does not: nearly every simcha has exactly one picture — its own —
   * and a chip on each of them saying so would be a label on the obvious. It
   * appears once there is a choice to have been made.
   */
  const saysMain = isMain && !!coverAnchor && (coverAnchor.kind === 'recipe' || anchorPictures(moments, coverAnchor).length > 1);
  const makeMain = async () => {
    if (!photo || !coverAnchor) return;
    setBusy(true);
    const res = coverAnchor.kind === 'recipe' ? await repos.recipes.setCover(coverAnchor.id, photo.id) : await repos.photos.setCover(coverAnchor, photo.id);
    setBusy(false);
    if (!res.ok) return fail(res);
    toast(t(coverAnchor.kind === 'recipe' ? 'recipe.pictures.madeMain' : 'memories.madeMain'));
    reload();
    // Closed, so that what they see next is the thing wearing it.
    onClose();
  };

  const belongsTo = (() => {
    const a = photo?.addedTo;
    // Opened from inside the very thing it belongs to: "Part of …" would lead where you already are.
    if (!a || inside) return null;
    if (a.kind === 'moment') {
      const m = moments.find((x) => x.id === a.id);
      return m ? { anchor: a, name: m.title || (m.milestone ? t(`milestone.${m.milestone}` as 'milestone.wedding') : t('photos.eventMark')) } : null;
    }
    if (a.kind === 'story') {
      const st = stories.find((x) => x.id === a.id);
      return st ? { anchor: a, name: st.title || t('story.untitled') } : null;
    }
    if (a.kind === 'recipe') {
      const r = recipes.find((x) => x.id === a.id);
      return r ? { anchor: a, name: r.doc.title || t('recipe.untitled') } : null;
    }
    const p = peopleById.get(a.id);
    return p ? { anchor: a, name: t('memories.birthTitle', { name: displayName(p, lang) }) } : null;
  })();

  return (
    <Dialog
      open={!!photo}
      onClose={() => { setEditing(false); onClose(); }}
      /*
       * What this one thing is, not which tab it came from. A voice note said
       * "Photos" at the top, which was the tab's name standing in for a name of
       * its own; a recording is a Recording, a clip is a Video, a picture is a
       * Photo — and anything the family actually named says that instead.
       */
      title={photo?.title || photo?.caption || (photo?.isEvent ? t('photos.eventMark') : photo && isAudio(photo) ? t('voice.note') : photo && isVideo(photo) ? t('memories.video') : t('memories.photo'))}
      labelledBy="photo-view-title"
      wide
      actions={
        photo && editing ? (
          <>
            <button type="button" className="btn btn-secondary" onClick={() => setEditing(false)}>{t('nav.cancel')}</button>
            <button type="button" className="btn btn-primary" onClick={() => void saveEdit()} disabled={busy || (eEvent && !eTitle.trim() && !photo.storageKey)}>
              <Icon name="check" />
              <span>{t('nav.save')}</span>
            </button>
          </>
        ) : photo ? (
          <>
            {canEdit && (
              <button type="button" className="btn btn-secondary" onClick={startEdit}>
                <Icon name="edit" />
                <span>{t('photos.editDetails')}</span>
              </button>
            )}
            {mayChooseMain && !isMain && (
              /* Violet when it is only theirs to press because Admin mode is on — like every other such power. */
              <button type="button" className={`btn btn-primary ${mainRight === 'admin' ? 'is-admin-control' : ''}`} onClick={() => void makeMain()} disabled={busy}>
                <Icon name="star" />
                <span>{t(coverAnchor?.kind === 'recipe' ? 'recipe.pictures.makeMain' : 'memories.makeMain')}</span>
              </button>
            )}
            {admin && (
              <button type="button" className="btn btn-secondary is-admin-control" onClick={() => setRemoving(true)}>
                <Icon name="trash" />
                <span>{t('decisions.removePicture')}</span>
              </button>
            )}
          </>
        ) : undefined
      }
    >
      {photo && editing && (
        <div className="stack-lg">
          {photo.storageKey ? <PhotoImage photo={photo} className="photo-preview" /> : null}
          {(eEvent || photo.title) && (
            <div className="field">
              <label className="field-label" htmlFor="e-title">{t('photos.eventTitle')}</label>
              <input id="e-title" className="input" value={eTitle} onChange={(e) => setETitle(e.target.value)} placeholder={t('photos.eventTitleHint')} />
            </div>
          )}
          <div className="field">
            <label className="field-label" htmlFor="e-caption">{eEvent ? t('photos.eventNotes') : t('photos.caption')} <span className="faint">({t('common.optional')})</span></label>
            <input id="e-caption" className="input" value={eCaption} onChange={(e) => setECaption(e.target.value)} />
          </div>
          {/* Something added to a day, a story or a recipe belongs to it and is never an occasion of its own — so it is not asked. */}
          {photo.storageKey && !photo.addedTo && (
            <label className={`radio-card ${eEvent ? 'selected' : ''}`}>
              <input type="checkbox" checked={eEvent} onChange={(e) => setEEvent(e.target.checked)} />
              <span className="title">{t('photos.isEvent')}</span>
            </label>
          )}
          <FuzzyDateInput value={eTakenOn} onChange={setETakenOn} kinds={['exact', 'month', 'year', 'unknown']} label={eEvent ? t('photos.eventWhen') : t('photos.when')} compact defaultToToday />
        </div>
      )}
      {photo && !editing && (
        <div className="stack moment-body">
          {photo.storageKey ? <PhotoImage photo={photo} className="photo-full" playable /> : <div className={`event-card-big ${photo.milestone ? 'is-milestone' : ''}`}><Icon name={photo.milestone ? 'milestone' : 'sparkle'} /><span>{photo.takenOn ? formatFuzzyDate(photo.takenOn, lang) : ''}</span></div>}
          {photo.title && photo.caption ? <p>{photo.caption}</p> : null}

          {/*
            * Who is in it and who shared it, in the same shape a story uses for
            * its own facts: a quiet label, then circles. Names apiece turned
            * four people into four rows, and the face is what a family
            * recognises anyway — the names stay on each circle for anyone
            * hovering or listening.
            */}
          {!tagging && (
            <dl className="facts">
              <div className="fact">
                <dt>{t('photos.inPhoto')}</dt>
                {/* The day sits at the far end of this row — under the corner of the picture. */}
                <dd className="fact-spread">
                  <span className="row-nowrap">
                  {pinned.length > 0 && (
                    <span className="story-top-faces" role="group" aria-label={inPhotoLabel}>
                      {pinned.map((p) => (
                        <button key={p.id} type="button" className="story-mini" onClick={() => openPersonCard(p.id)} aria-label={displayName(p, lang)} title={displayName(p, lang)}>
                          <Avatar person={p} lang={lang} size="sm" />
                        </button>
                      ))}
                    </span>
                  )}
                  <button type="button" className="photo-who-add" onClick={startTagging} aria-label={t('photos.tagSomeone')} title={t('photos.tagSomeone')}>
                    <Icon name="plus" />
                  </button>
                  </span>
                  {photo.takenOn && <span className="fact-text faint">{formatFuzzyDate(photo.takenOn, lang)}</span>}
                </dd>
              </div>
              <div className="fact">
                <dt>{t('photos.sharedByLabel')}</dt>
                <dd>
                  {uploaderPerson ? (
                    <span className="story-top-faces" role="group" aria-label={sharedLabel}>
                      <button type="button" className="story-mini" onClick={() => openPersonCard(uploaderPerson.id)} aria-label={displayName(uploaderPerson, lang)} title={displayName(uploaderPerson, lang)}>
                        <Avatar person={uploaderPerson} lang={lang} size="sm" />
                      </button>
                    </span>
                  ) : (
                    <span className="fact-text">{uploader}</span>
                  )}
                  <span className="faint xs">{relativeTime(photo.createdAt, lang)}</span>
                </dd>
              </div>
            </dl>
          )}

          {/* A wordless response: one emoji each; tap again to take it back. The same
              folded pill as a story carries — one control, one look, everywhere. */}
          <ReactionBar reactions={photo.reactions} label={t('photos.react')} addLabel={t('photos.react')} onReact={(e) => void react(e)} />

          {tagging && (
            <div className="photo-people">
              <>
                <div className="photo-people-head">
                  <span className="bold">{t('photos.tagTitle')}</span>
                  {me && !draftIds.includes(me.id) && (
                    <button type="button" className="btn btn-sm btn-secondary" onClick={() => setDraftIds((d) => [...d, me.id])}>
                      <Icon name="pin" />
                      <span>{t('photos.pinMe')}</span>
                    </button>
                  )}
                </div>
                <PersonPicker
                  people={people}
                  value={tagPick}
                  onChange={(id) => {
                    setTagPick(null);
                    if (id) setDraftIds((d) => (d.includes(id) ? d : [...d, id]));
                  }}
                  label={t('photos.pinSomeone')}
                  placeholder={t('photos.pinSomeoneHint')}
                  exclude={draftIds}
                  allowClear
                  autoFocus
                />
                {draftIds.length > 0 ? (
                  <div className="chip-row">
                    {draftIds.map((pid) => {
                      const p = peopleById.get(pid);
                      return p ? (
                        <span key={pid} className="chip photo-person">
                          <Avatar person={p} lang={lang} size="sm" />
                          <span>{displayName(p, lang)}</span>
                          <button type="button" className="chip-x" onClick={() => setDraftIds((d) => d.filter((x) => x !== pid))} aria-label={t('photos.unpin', { name: displayName(p, lang) })} title={t('photos.unpin', { name: displayName(p, lang) })}>
                            <Icon name="close" />
                          </button>
                        </span>
                      ) : null;
                    })}
                  </div>
                ) : (
                  <p className="small muted" style={{ margin: 0 }}>{t('photos.tagEmpty')}</p>
                )}
                <div className="row">
                  <button type="button" className="btn btn-primary" onClick={() => void saveTags()} disabled={busy}>
                    <Icon name="check" />
                    <span>{t('nav.save')}</span>
                  </button>
                  <button type="button" className="btn btn-ghost" onClick={() => setTagging(false)}>{t('nav.cancel')}</button>
                </div>
              </>
            </div>
          )}

          {/*
            * What it is part of — a day the family remembers, a story, a recipe —
            * with the rest of that one tap away; and, for a recipe's picture, whether
            * this is the one that stands for it.
            */}
          {(saysMain || belongsTo) && (
            <div className="row">
              {saysMain && (
                /* Olive is the kitchen's colour and nothing else's: a day's main picture wears the simcha's gold. */
                <span className={`chip ${coverAnchor?.kind === 'recipe' ? 'chip-recipe' : 'chip-milestone'}`}>
                  <Icon name="star" />
                  <span>{t(coverAnchor?.kind === 'recipe' ? 'recipe.pictures.main' : 'memories.main')}</span>
                </span>
              )}
              {belongsTo && (
                <button
                  type="button"
                  className={`chip chip-btn ${belongsTo.anchor.kind === 'recipe' ? 'chip-recipe' : 'chip-milestone'}`}
                  onClick={() => { onClose(); openMemories(belongsTo.anchor); }}
                >
                  <Icon name={belongsTo.anchor.kind === 'story' ? 'stories' : belongsTo.anchor.kind === 'recipe' ? 'recipe' : 'milestone'} />
                  <span dir="auto">{t('memories.partOf', { name: belongsTo.name })}</span>
                  <Icon name="forward" className="icon-mirror" />
                </button>
              )}
            </div>
          )}

          {(photo.milestone || photo.isEvent) && (
            <div className="small muted">
              {photo.milestone ? (
                <span className="chip chip-milestone xs"><Icon name="milestone" />{t(`milestone.${photo.milestone}` as 'milestone.wedding')}</span>
              ) : (
                <span className="chip chip-memory xs"><Icon name="sparkle" />{t('photos.eventMark')}</span>
              )}
            </div>
          )}

          {/* Removing keeps the picture — it leaves the family's view and stays
              in Admin controls → Admin decisions, with whatever reason was given. */}
          <ReasonDialog
            open={removing}
            title={t('decisions.removePicture')}
            body={t('decisions.removePictureHelp')}
            confirmLabel={t('decisions.removePicture')}
            onClose={() => setRemoving(false)}
            onConfirm={(reason) => {
              void repos.admin.removeMoment(photo.id, reason).then((r) => {
                if (r.ok) {
                  toast(t('decisions.removed'));
                  reload();
                  onClose();
                } else toast(t(('messageKey' in r ? r.messageKey : 'error.unexpected') as Parameters<typeof t>[0]));
              });
            }}
          />
        </div>
      )}
    </Dialog>
  );
}

/**
 * A moment's picture — or its clip: the family adds videos to a milestone
 * alongside the photos, and both live in the same store, so the mime type
 * decides which element to draw. On a tile the clip is a muted first frame
 * with a play mark over it; opened, it gets real controls (`playable`).
 */
export function PhotoImage({ photo, className = '', playable, thumb }: { photo: FamilyPhoto; className?: string; playable?: boolean;
  /**
   * Draw it small: a circle on a constellation wants a few kilobytes, not the
   * 1600px original. The repository makes the small square once and keeps it
   * (`photos.getThumbUrl`), and anything that is not a still image is handed
   * the real thing because there is nothing to shrink. `'medium'` is the whole
   * picture at card size — the top of a recipe's card, a day on the calendar.
   */
  thumb?: boolean | 'medium';
}) {
  const repos = useRepos();
  const [src, setSrc] = useState<string | undefined>();
  /*
   * A url that will not load gets exactly one second chance.
   *
   * The repository remembers the url it handed out, which is what keeps a ring of
   * two hundred circles cheap — and what turns one stale url into a broken
   * picture for the rest of the session. In the prototype a url points into the
   * browser's own blob storage, whose backing is let go when the database
   * connection is dropped and reopened (a second tab is enough); in production a
   * signed url expires. Either way the picture is safe in storage and only the
   * url is dead, so a failure forgets it (`forgetUrls`) and asks again. If the
   * second one fails too, the grey placeholder is the honest answer — never the
   * browser's broken-picture mark, which says "this app lost your photograph".
   */
  const [attempt, setAttempt] = useState(0);
  const healed = useRef(false);
  useEffect(() => {
    let cancelled = false;
    void (thumb ? repos.photos.getThumbUrl(photo.id, thumb === 'medium' ? 'medium' : 'small') : repos.photos.getUrl(photo.id)).then((url) => {
      if (!cancelled) setSrc(url);
    });
    return () => {
      cancelled = true;
    };
  }, [repos, photo.id, thumb, attempt]);
  const onError = () => {
    if (healed.current) {
      setSrc(undefined);
      return;
    }
    healed.current = true;
    repos.photos.forgetUrls(photo.id);
    setSrc(undefined);
    setAttempt((n) => n + 1);
  };
  if (!src) return <span className={`photo-placeholder ${className}`} />;
  if (photo.mimeType?.startsWith('video/')) {
    return <video className={className} src={src} controls={playable} muted={!playable} playsInline preload="metadata" onError={onError} />;
  }
  /*
   * A voice note has nothing to look at, so it draws itself: a microphone where
   * the picture would be, and a real player once somebody opens it.
   */
  if (photo.mimeType?.startsWith('audio/')) {
    return playable ? (
      <audio className={`photo-audio-player ${className}`} src={src} controls preload="metadata" onError={onError} />
    ) : (
      <span className={`photo-audio ${className}`} aria-hidden="true"><Icon name="mic" /></span>
    );
  }
  return <img className={className} src={src} alt="" draggable={false} decoding="async" onError={onError} />;
}
